import { makeStyles } from '@material-ui/styles'
import FontAwesomeIcon from 'pf9-ui-components/built/components/FontAwesomeIcon'
import { ValidatedFormProps } from 'pf9-ui-components/built/components/validatedForm/model'
import withFormContext from 'pf9-ui-components/built/components/validatedForm/withFormContext'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import React, { FC, useMemo } from 'react'
import { listFlavors } from 'app/plugins/openstack/components/flavors/actions'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { listNetworks } from 'app/plugins/openstack/components/networks/actions'
import { networksSelector } from 'app/plugins/openstack/components/networks/selectors'
import { listStoragePools } from 'app/plugins/openstack/components/storage/pools/actions'
import { storagePoolsSelector } from 'app/plugins/openstack/components/storage/pools/selectors'
import { useAppSelector } from 'app/store'
import { compose } from 'app/utils/fp'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { DEFAULT_VOLUME_SIZE, filterFlavorsBasedOnSourceType } from './helpers'
import NodePoolConfigCard from './NodePoolConfigCard'

export const UNSUPPORTED_VM_FLAVORS = ['pf9.unknown', 'm1.tiny', 'm1.small']

interface ServerConfigurationFieldProps extends ValidatedFormProps {
  params: any
  getParamsUpdater: (key: string) => (value: unknown) => void
  nodePoolErrors: NodePoolErrors
  setIsGPUFlavorSelected: (value: boolean) => void
}

export interface NodePoolErrors {
  [id: string]: { fieldId: string; message: string }
}

const ServerConfigurationField = ({
  params,
  getParamsUpdater,
  nodePoolErrors = undefined,
  setIsGPUFlavorSelected,
}: ServerConfigurationFieldProps) => {
  const classes = useStyles()
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()

  // VM Flavors
  const { loading: loadingVmFlavors } = useListAction(listFlavors, {
    params,
  })
  // Filter out unsupported VM Flavors
  const flavors = useAppSelector(flavorsSelector)
  const filteredFlavors = useMemo(
    () =>
      flavors?.filter(
        (vm) => !UNSUPPORTED_VM_FLAVORS.includes(vm?.name) && vm?.vcpus > 0 && vm?.ram > 0,
      ),
    [flavors],
  )

  // Storage Pools
  const storagePools = useAppSelector(storagePoolsSelector)

  // Networks
  const { loading: loadingNetworks } = useListAction(listNetworks)
  const allNetworks = useAppSelector(networksSelector)

  // Filter networks by tenant
  const networks = useMemo(() => {
    return allNetworks?.filter((network) => {
      return network?.project_id === currentTenant || network?.shared
    })
  }, [allNetworks, currentTenant])

  const handleAddNewNodePool = () => {
    const id = params?.nodePools[params?.nodePools?.length - 1]?.id + 1
    getParamsUpdater('nodePools')([...params.nodePools, { id, volumeSize: DEFAULT_VOLUME_SIZE }])
  }

  const handleNodePoolChange = (id) => (newValues) => {
    const updatedNodePools = params?.nodePools?.map((nodePool) => {
      if (nodePool?.id === id) {
        return { ...nodePool, ...newValues }
      }
      return nodePool
    })

    // Update gpuAddons based on updatedNodePools
    const gpuSupported = updatedNodePools?.some((nodePool) => nodePool?.gpuSupported)
    setIsGPUFlavorSelected(gpuSupported)
    getParamsUpdater('gpuAddons')(gpuSupported)
    getParamsUpdater('nodePools')(updatedNodePools)
  }

  const handleDeleteNodePool = (id) => {
    const updatedNodePools = params?.nodePools?.filter((nodePool) => nodePool?.id !== id)
    getParamsUpdater('nodePools')(updatedNodePools)
  }

  return (
    <div>
      {params?.nodePools?.map((nodePool) => {
        const id = nodePool?.id
        return (
          <NodePoolConfigCard
            key={id}
            nodePool={nodePool}
            onChange={handleNodePoolChange(id)}
            vmFlavors={filteredFlavors}
            storagePools={storagePools}
            networks={networks}
            showDeleteButton={params?.nodePools?.length > 1}
            onDeleteNodePool={handleDeleteNodePool}
            errors={nodePoolErrors[id]}
            loadingVmFlavors={loadingVmFlavors}
            getParamsUpdater={getParamsUpdater}
            params={params}
          />
        )
      })}
      <div className={classes.addNodePoolButton}>
        <FontAwesomeIcon className={classes.plusIcon} onClick={handleAddNewNodePool} size="xl">
          plus-circle
        </FontAwesomeIcon>
        <Text variant="body2">Add Node Group</Text>
      </div>
    </div>
  )
}

export default compose(withFormContext)(
  ServerConfigurationField,
) as FC<ServerConfigurationFieldProps>

const useStyles = makeStyles<Theme>((theme) => ({
  addNodePoolButton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    margin: theme.spacing(4, 0),
    alignItems: 'center',
  },
  plusIcon: {
    color: theme.palette.blue[700],
    marginRight: theme.spacing(1),
    fontWeight: 900,
  },
}))
